"use client";
import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import {
  ShoppingCartOutlinedIcon,
  PaymentIcon,
  NotificationsActiveIcon,
} from '../icons';
import { WishlistToggle } from './WishlistToggle';
import { ProductUpdatesToggle } from './ProductUpdatesToggle';
import styles from './StyledComponents.module.css';

/**
 * Flags <body> while this component is mounted so global CSS can give the
 * page extra bottom padding (so the sticky mobile action bar doesn't cover
 * the footer's last row of content). See `body.has-product-action-bar`
 * rule in app/globals.css.
 */
function useFlagActionBarPresent() {
  useEffect(() => {
    document.body.classList.add('has-product-action-bar');
    return () => {
      document.body.classList.remove('has-product-action-bar');
    };
  }, []);
}

interface ActionButtonsProps {
  productId: string;
  outOfStock: boolean;
  loadingPrice: boolean;
  hasPrice: boolean;
  canAddToCart: boolean;
  cartError: string | null;
  onAddToCart: () => void;
  onBuyNow: () => void;
  /** Opens the product updates modal: the sold-out button and the bell. */
  onNotify: () => void;
  updatesActive: boolean;
}

export const ActionButtons = React.memo<ActionButtonsProps>(({
  productId,
  outOfStock,
  loadingPrice,
  hasPrice,
  canAddToCart,
  cartError,
  onAddToCart,
  onBuyNow,
  onNotify,
  updatesActive,
}) => {
  const tCommon = useTranslations('common');
  useFlagActionBarPresent();

  return (
    <>
      {cartError && (
        <div className={styles.helperText} style={{ color: 'var(--color-danger)' }}>
          {cartError}
        </div>
      )}

      <div className={styles.actionButtonsContainer}>
        {outOfStock ? (
          <button
            className={styles.actionButton}
            onClick={onNotify}
            style={{ gridColumn: '1 / 3' }}
          >
            <NotificationsActiveIcon fontSize="small" />
            {tCommon('buttons.notifyWhenBack')}
          </button>
        ) : (
          <>
            <button
              className={styles.actionButton}
              onClick={onBuyNow}
              disabled={loadingPrice || !hasPrice || !canAddToCart}
            >
              <PaymentIcon fontSize="small" />
              {tCommon('buttons.buyNow')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.outline}`}
              onClick={onAddToCart}
              disabled={loadingPrice || !hasPrice || !canAddToCart}
            >
              <ShoppingCartOutlinedIcon fontSize="small" />
              {tCommon('buttons.addToCart')}
            </button>
          </>
        )}
        <WishlistToggle productId={productId} />
        <ProductUpdatesToggle active={updatesActive} onClick={onNotify} />
      </div>
    </>
  );
});

ActionButtons.displayName = 'ActionButtons';
